<template>
  <div class="live-messages">
    <v-toolbar color="transparent" class="live-messages__toolbar elevation-0">
      <v-spacer></v-spacer>
      <v-toolbar-items class="mt-2">
        <v-tooltip bottom>
          <v-btn icon color="primary" flat @click="generateNewMessage()" slot="activator">
            <v-icon>add</v-icon>
          </v-btn>
          <span>{{ $t('message.common.add') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn icon color="primary" flat @click="getMessages()" slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>{{ $t('message.common.refresh') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <v-btn icon color="primary" flat v-if="!contentId"
                 :to="{ name: 'content.liveStandalone', params: {type: liveType, id: liveId} }"
                 target="_blank"
                 slot="activator">
            <v-icon>open_in_new</v-icon>
          </v-btn>
          <span>{{ $t('message.content.live.actions.standalone') }}</span>
        </v-tooltip>
      </v-toolbar-items>
    </v-toolbar>
    <v-data-table
      :headers="dataTableHeader"
      :must-sort="true"
      :items.sync="messages"
      :loading="loading"
      :total-items.sync="totalMessages"
      :pagination.sync="defaultPagination"
      :rows-per-page-items="[10,25,50]"
      @update:pagination="paginationChange"
      class="live-messages__data-table elevation-0"
    >
      <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <component
          :is="rowComponent"
          :item="props.item"
          @publish="publishUnpublishMessage(props.item)"
          @edit="setMessageToBeEdited(props.item)"
          @delete="deleteMessageIfConfirms(props.item)"
        ></component>
      </template>
    </v-data-table>
    <nx-fullscreen-dialog
      v-if="modalIsShown"
      :title="$t(formDialogTitle)|capitalize "
      :active="modalIsShown"
      @dismiss="resetEditor()"
    >
      <template slot="toolbar-items">
        <component
          :is="messageEditorDialogButtonsComponent"
          :value="messageThatIsBeingEdited"
          :loading="loading"
          :disabled="loading || form === undefined || !form.valid"
          :messageThatIsBeingEdited="messageThatIsBeingEdited"
          @accept="uploadMessage"
        ></component>
      </template>

      <component
        :is="messageFormComponent"
        :value="messageThatIsBeingEdited"
      ></component>
    </nx-fullscreen-dialog>
    <nx-dialog-confirm
      :title="$t('message.content.editor.unpublishLiveMessageTitle')"
      :text="$t('message.content.editor.unpublishLiveMessageText')"
      ref="confirmUnpublish"
    ></nx-dialog-confirm>
    <nx-dialog-confirm
      :title="$t('message.content.editor.publishLiveMessageTitle')"
      :text="$t('message.content.editor.publishLiveMessageText')"
      ref="confirmPublish"
    ></nx-dialog-confirm>
    <nx-dialog-confirm
      :title="$t('message.content.editor.deleteLiveMessageTitle')"
      :text="$t('message.content.editor.deleteLiveMessageText')"
      ref="confirmDelete"
    ></nx-dialog-confirm>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import FullscreenDialog from '@/components/dialogs/FullscreenDialog';
import Confirm from '@/components/dialogs/Confirm';
import BasePage from '@/pages/BasePage';
import NxFormSwitch from '@/modules/form/components/FormSwitch';
import getMessageFormComponent from '@/modules/live-messages/config/get-message-form-component';
import liveMessagesConfig from '@/modules/live-messages/config';

export default {
  name: 'nx-live-messages-data-table',
  extends: BasePage,
  inject: {
    reloadPreview: {
      default() {
        return () => {};
      },
    },
    $getParentEditor: {
      default() {
        return () => ({});
      },
    },
  },
  props: {
    contentId: {
      type: Number,
      required: false,
    },
    contentType: {
      type: String,
      required: false,
    },
  },
  components: {
    [FullscreenDialog.name]: FullscreenDialog,
    [Confirm.name]: Confirm,
    [NxFormSwitch.name]: NxFormSwitch,
  },
  data() {
    return {
      filters: undefined,
      formDialogTitle: '',
      loading: false,
      // Needed in order to place the messages in order or creation when creating the data-table
      defaultPagination: {
        sortBy: 'publicationDate',
        descending: true,
        rowsPerPage: this.contentId ? 25 : 10,
      },
      isActive: false,
    };
  },
  computed: {
    ...mapGetters('live', [
      'dataTableHeader',
    ]),
    ...mapState('live', [
      'messageThatIsBeingEdited',
      'totalMessages',
      'modalIsShown',
      'messages',
      'form',
    ]),
    liveId() {
      return this.contentId ? Number.parseInt(this.contentId, 10) : this.$getParentEditor().id;
    },
    liveType() {
      return this.contentType || this.$getParentEditor().type;
    },
    messageFormComponent() {
      return getMessageFormComponent(this.liveType);
    },
    rowComponent() {
      return liveMessagesConfig.dataTableConfig.rowComponent;
    },
    messageEditorDialogButtonsComponent() {
      return liveMessagesConfig.dataTableConfig.messageEditorDialogButtonsComponent;
    },
  },
  methods: {
    async getMessages() {
      if (!this.liveId) {
        return;
      }
      this.loading = true;
      await this.$store.dispatch('live/GET_TABLE_DATA', this.filters);
      this.reloadPreview();
      this.loading = false;
    },
    async createMessage() {
      await this.$store.dispatch('live/CREATE_MESSAGE', this.messageThatIsBeingEdited);
      this.resetEditor();
      this.getMessages();
    },
    async updateMessage() {
      await this.$store.dispatch('live/UPDATE_MESSAGE', this.messageThatIsBeingEdited);
      this.resetEditor();
      this.getMessages();
    },
    /**
     * Resets the editor values in the store so we can start editing another message
     */
    resetEditor() {
      this.$store.dispatch('live/RESET_EDITOR');
      this.$store.dispatch('live/SET_FORM', undefined);
    },
    /**
     * Generates a basic message to send to the API to create a new message
     */
    generateNewMessage() {
      const message = {
        publicationDate: moment().format(),
        body: [],
        contentId: this.liveId,
        published: true,
      };
      this.setMessageToBeEdited(message);
    },
    /**
     * We store in the store all the data needed to edit a message
     */
    async setMessageToBeEdited(message) {
      if (message.id !== undefined) { // Message was already created
        this.formDialogTitle = 'message.common.edit';
        await this.$store.dispatch('live/GET_MESSAGE', message.id);
        if (this.messageThatIsBeingEdited === undefined) {
          this.$store.commit('live/SET_MODAL_IS_SHOWN', false);
        }
        return;
      }

      this.formDialogTitle = 'message.common.add';
      this.$store.dispatch('live/PUT_MESSAGE_IN_EDITOR', message);
    },
    async deleteMessageIfConfirms(message) {
      this.loading = true;
      if (await this.$refs.confirmDelete.open()) {
        await this.$store.dispatch('live/DELETE_MESSAGE', message);
        this.getMessages();
      }
      this.loading = false;
    },
    async publishUnpublishMessage(message) {
      let agree = false;
      if (message.published) {
        agree = await this.$refs.confirmUnpublish.open();
      } else {
        agree = await this.$refs.confirmPublish.open();
      }
      if (agree) {
        await this.$store.dispatch('live/PUBLISH_UNPUBLISH_MESSAGE', message);
        this.getMessages();
      }
    },
    async uploadMessage() {
      if (this.loading) return;
      this.loading = true;
      if (this.messageHasBeenEdited()) {
        if (this.messageThatIsBeingEdited.id !== undefined) {
          await this.updateMessage();
        } else {
          await this.createMessage();
        }
      } else {
        this.resetEditor();
      }
      this.loading = false;
    },
    /**
     * Compares the orginal message that we're editing with the copy in which we're making the changes
     */
    messageHasBeenEdited() {
      return JSON.stringify(this.$store.state.live.messageToBeEdited)
        !== JSON.stringify(this.messageThatIsBeingEdited);
    },
    /**
     * Generates a new filter when triggered in the data-table and fetches the messages
     */
    paginationChange(data) {
      this.filters = {
        liveId: this.liveId,
        limit: data.rowsPerPage,
        page: data.page,
      };

      if (data.sortBy) {
        this.filters.sort = [
          { field: data.sortBy, criteria: data.descending ? 'DESC' : 'ASC' },
        ];
      }

      this.getMessages();
    },
  },
  async mounted() {
    if (this.contentId) this.finishLoading();
    await this.$store.dispatch('live/SET_LIVE', { id: this.liveId, type: this.liveType });
    this.getMessages();
  },
};
</script>
<style>
.live-messages {
  margin-bottom: 20px;
}

.live-messages__data-table i.v-icon.fa {
  font-size: 24px;
  font-weight: 400;
  font-style: normal;
}

.live-messages__data-table table.v-table thead th:first-child,
.live-messages__data-table table.v-table thead th:not(:nth-child(1)),
.live-messages__data-table table.v-table tbody td:first-child,
.live-messages__data-table table.v-table tbody td:not(:nth-child(1)) {
  padding: 0 10px;
}

.live-messages__data-table table.v-table thead th:first-child,
.live-messages__data-table table.v-table thead th:not(:nth-child(1)) {
  font-weight: bold;
}

.live-messages__data-table--actions i.v-icon.fa {
  display: flex;
}

.live-messages__data-table tr > td:not(:last-child) {
  max-width: 100px;
}

.live-messages__data-table tr > td.live-messages__data-table--creation-staff {
  max-width: 120px;
}

.live-messages__data-table td {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.state--unpublished {
  transform: rotate(180deg);
}
</style>
